import { EventSeriesOrderData } from '@beat-venues/common-types';
import { Container, EventCard, Heading } from '@beat-venues/ui/react';
import { useQuery } from '@tanstack/react-query';
import { type ReactElement, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { LayoutNoEvents } from '../../components';
import { UserContext } from '../../context/user-context';
import withTransaction, {
    WithTransactionProps,
} from '../../hoc/withTransaction';
import { useAnalytics } from '../../hooks/use-analytics';

const Events: React.FC<WithTransactionProps> = ({
    transactions,
}): ReactElement => {
    const { t, i18n } = useTranslation();
    const { user } = useContext(UserContext);

    const { data: pastEvents } = useQuery({
        queryKey: ['my-past-events', i18n.language],
        queryFn: () =>
            fetch(
                `/api/events/${user?.sfContactId}?lang=${i18n.language}&isPast=true`
            ).then((res) => {
                if (res.ok) {
                    return res.json();
                } else return [];
            }),
        enabled: !!user?.sfContactId,
    });

    const { triggerPageView } = useAnalytics();

    useEffect(() => {
        triggerPageView();
    }, []);

    return (
        <Container className="mb-12">
            {(transactions?.length === 0 && pastEvents?.length === 0) ||
            !user?.sfContactId ? (
                <LayoutNoEvents />
            ) : (
                <Heading as="h1" className="font-light xl:ml-0">
                    {t('events.myEvents')}
                </Heading>
            )}
            {transactions?.length > 0 && (
                <>
                    <Heading as="h2">{t('events.future')}</Heading>
                    <div className="flex grid-cols-3 flex-col flex-wrap gap-2 gap-y-2 md:gap-x-4 lg:grid lg:gap-y-6">
                        {transactions?.map((event, index) => (
                            <Link
                                key={`${event.slug}-${index}`}
                                to={`${t('routes.events')}/${event.orderId}-${
                                    event.eventId
                                }`}
                            >
                                <EventCard
                                    basePath={t('routes.events')}
                                    className="flex-1"
                                    date={event?.date}
                                    eventId={event.eventId}
                                    image={event?.image}
                                    isPast={false}
                                    isVip={event?.isVip}
                                    name={event.name}
                                    orderId={event.orderId}
                                    size="sm"
                                    statusLabel={event.statusLabel}
                                    venueId={event?.venueId}
                                    venueName={event?.venueName}
                                />
                            </Link>
                        ))}
                    </div>
                </>
            )}
            {pastEvents?.length > 0 && (
                <>
                    <Heading as="h2" className="mt-12">
                        {t('events.past')}
                    </Heading>
                    <div className="flex grid-cols-3 flex-col flex-wrap gap-2 gap-y-2 md:gap-x-4 lg:grid lg:gap-y-6">
                        {pastEvents?.map(
                            (event: EventSeriesOrderData, index: number) => (
                                <EventCard
                                    key={`${event.slug}-${index}`}
                                    basePath=""
                                    className="flex-1"
                                    date={event?.date}
                                    image={event?.image}
                                    isPast={true}
                                    isVip={event?.isVip}
                                    name={event.name}
                                    size="sm"
                                    slug={event.slug}
                                    venueId={event?.venueId}
                                    venueName={event?.venueName}
                                />
                            )
                        )}
                    </div>
                </>
            )}
        </Container>
    );
};

const EventsPage = withTransaction(Events);
export { EventsPage };
